/**
 * Phase 31 Plan 31-04 — KanbanSwimlane: CSS-Grid swimlane that replaces
 * MilestoneSidebar + KanbanBoard + KanbanColumn (deletions deferred to Plan 05).
 *
 * Layout:
 *   - Root is a flex column with two children: the sticky header band and the
 *     body grid. Both children are separate CSS grids driven by the same
 *     `gridStyle` (`gridTemplateColumns`), so their column tracks resolve
 *     identically and stay aligned (260911-nh1 / MKS-173 — pinned headers).
 *   - Header band = column-header strip (sticky-left empty corner + N
 *     `ColumnHeaderCell`s wrapped in `SortableContext` for column reorder),
 *     pinned via `sticky`/`-top-4`/`z-20`. It is a flex child, not a grid
 *     item — a grid item's containing block is its own grid area (one row
 *     tall), which would give a pinned header zero travel.
 *   - Body grid rows = one `KanbanSwimlaneRow` per milestone (DESC by start_date
 *     from the backend, no client re-sort) + one synthetic "No milestone" row
 *     pinned at the bottom.
 *   - MKS-184: every cell is its own vertical scroll container, so one long
 *     column no longer sets the height of the board. A board with no
 *     milestones (`fillViewportHeight`) renders its single row inside an
 *     explicit `minmax(0, 1fr)` row track and the page stops scrolling
 *     vertically altogether; a board WITH milestones keeps the page scroll
 *     (and with it the sticky header band's travel) and caps each cell at
 *     roughly one viewport instead — variant (a) of the task.
 *
 * Each (milestone, taskState) cell registers its own `useDroppable` so
 * KanbanBoardPage's onDragEnd can route drops via `over.data.current.type ===
 * 'swimlane-cell'` (CONTEXT D-01 / UI-SPEC §3).
 *
 * Visual contract donors:
 *   - row-header chrome: MilestoneSidebar.tsx:200-281
 *   - cell isOver ring: KanbanColumn.tsx:88-93
 *   - SortableContext for columns: KanbanBoard.tsx:50-69 (relocated)
 *
 * Pitfall 5: row header is `<div role="button">` (NOT `<button>`) because
 * pencil/trash are nested `<button>`s — nesting `<button>` inside `<button>`
 * is invalid HTML. Pitfall 6: each hover-icon onClick `e.stopPropagation()`
 * so editing/deleting does NOT toggle fold.
 *
 * Active-row treatment: ALL rows render `border-l-2 border-transparent`
 * (UI-SPEC line 332 lock — Phase 31 has NO selection accent).
 */
import { useDroppable } from '@dnd-kit/core';
import {
  useSortable,
  SortableContext,
  horizontalListSortingStrategy,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Bars3Icon,
  CheckCircleIcon,
  ChevronDoubleLeftIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  PencilSquareIcon,
  PlayCircleIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import {
  CheckCircleIcon as CheckCircleSolidIcon,
  PlayCircleIcon as PlayCircleSolidIcon,
} from '@heroicons/react/24/solid';
import { useMemo, useState, type CSSProperties, type KeyboardEvent } from 'react';
import type {
  BoardColumn,
  BoardResponse,
  BoardSubproject,
  TaskCard as TaskCardData,
} from '../../services/api/board';
import type { Milestone } from '../../services/api/milestones';
import { useMilestones } from '../../hooks/useMilestones';
import { useFoldState } from '../../hooks/useFoldState';
import { useColumnCollapse } from '../../hooks/useColumnCollapse';
import { useMilestoneOrder } from '../../hooks/useMilestoneOrder';
import { useSlimHeaderPreference } from '../../hooks/useSlimHeader';
import { TaskCard } from './TaskCard';
import { AddTaskInlineForm } from './AddTaskInlineForm';
import { computeMilestoneCounts, formatDateRange } from './milestoneCounts';
import { StateColorPickerPopover } from '../domain/StateColorPickerPopover';
import type { TaskStalenessConfig } from '../../utils/format';
import { subprojectLaneKey, type SubprojectLane } from '../../utils/subprojectLanes';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface KanbanSwimlaneProps {
  projectId: string;
  boardData: BoardResponse;
  audience: 'internal' | 'external';
  canManageMilestones: boolean;
  canReorderColumns?: boolean;
  /**
   * Quick task 260610-kv6 — when true, ColumnHeaderCell renders inline
   * rename / recolor / delete affordances mirroring the project edit
   * page's StateListEditor. Same gate shape as canReorderColumns
   * (admin OR project PM on internal audience). Falsy → header is read-only.
   */
  canManageStates?: boolean;
  /**
   * Quick 260914-huu — show the vertical drag handle on each milestone row
   * and enable row-reorder via useSortable. A view-preference affordance
   * (private per-user ordering), NOT an authority gate like
   * canReorderColumns/canManageStates — every internal viewer may reorder
   * their own view. Gated on `audience === 'internal'` at the call site only
   * to avoid a guaranteed-fail PATCH for external viewers (their preferences
   * PATCH 401s by design).
   */
  canReorderRows?: boolean;
  onRenameState?: (stateId: number, newName: string) => void | Promise<void>;
  onRecolorState?: (stateId: number, hex: string) => void | Promise<void>;
  onDeleteState?: (stateId: number) => void | Promise<void>;
  /** 260723-tst — flip a state's terminal flag from the board column header. */
  onToggleTerminalState?: (stateId: number, next: boolean) => void | Promise<void>;
  /** 260726-wip — flip a state's "work in progress" flag from the board column header. */
  onToggleInProgressState?: (stateId: number, next: boolean) => void | Promise<void>;
  /**
   * MKS-500 — the slim/full header choice, owned by the page because the
   * control that flips it now lives in the board toolbar. Passing it makes
   * this component CONTROLLED: it renders no corner toggle of its own (the
   * corner cell goes back to being empty) and takes the choice from here.
   * Omitted (flag off) it keeps MKS-378's behaviour exactly: its own
   * remembered state, flipped by the button in the corner cell.
   */
  slimHeader?: boolean;
  /**
   * Quick task 260610-kv6 — optional trailing slot in the column-header
   * strip. When provided, the grid template adds one extra column
   * (matching the standard column width) and the slot renders there. Used
   * by KanbanBoardPage to mount AddStateColumn at the end of the strip so
   * + Add scrolls together with the real columns.
   */
  trailingHeaderSlot?: React.ReactNode;
  /**
   * Phase 41 Plan 41-08 (Surface 5d-A) — parent project's `taskTypeId`.
   * Forwarded down to each TaskCard so SLA-typed projects render the
   * inline dual-bar timer on every card (CONTEXT D-4). Per checker
   * I-09: KanbanBoardPage already loads the project for column-reorder
   * gating, so the value is read there and passed down — never
   * accessed via `task.project.taskTypeId` (that field does not exist
   * on TaskCardData / TaskWithStats).
   */
  projectTaskTypeId?: number | null;
  /**
   * 260913-jpy — per-project staleness indicator config, forwarded down to
   * each TaskCard. Optional; omitted means the disabled default at the
   * TaskCard call site.
   */
  stalenessConfig?: TaskStalenessConfig;
  /**
   * The task-state id of the column the active card currently belongs to,
   * or null when no card is being dragged. Currently unused inside the
   * swimlane (CONTEXT D-02 puts the drag-over signal on the **target cell**
   * only); kept on the props for parity with the legacy KanbanBoard surface
   * and to give Plan 05 a wiring slot for any future row-level treatment.
   */
  isDraggingFromState?: number | null;
  /**
   * MKS-487 — a task card is currently being dragged (dnd-kit drag in flight,
   * owned by KanbanBoardPage). While it is true every column hidden by the
   * empty-column rule is revealed, so an empty state is still a reachable drop
   * target; the columns go back into the strip when the drag ends.
   */
  isDraggingCard?: boolean;
  /**
   * MKS-184 — the board has no milestones, so the swimlane renders a single
   * row and can fill the board scroll viewport exactly instead of growing
   * with its tallest column. The decision is made by KanbanBoardPage (it owns
   * the same ['milestones', projectId] query and must switch its own row
   * wrapper from `min-h-full` to `h-full` in lockstep); this component ANDs it
   * with its own loaded-and-empty check so the fill grid template can never be
   * applied to a render that actually has milestone rows in it.
   */
  fillViewportHeight?: boolean;
  /**
   * MKS-419 — merged board only: the readable subprojects by id. A card whose
   * `projectId` is in the map is a subproject card: it gets the slug badge and
   * none of the PARENT's per-project card context (SLA bars, staleness config).
   */
  subprojectsById?: ReadonlyMap<number, BoardSubproject>;
  /**
   * MKS-419 — merged board only: one extra swimlane row per subproject WITH cards,
   * rendered after the parent's own rows (see buildSubprojectLanes). When set, the
   * parent's rows hold ONLY the parent's cards. Lane rows fold like milestone rows
   * (under the `sp:<id>` key), are not reorderable and offer no inline add.
   */
  subprojectLanes?: readonly SubprojectLane[];
  /**
   * MKS-419 — hide the parent's milestone rows and its "No milestone" row. Set while
   * the Project filter narrows the board to one subproject, so only that lane shows.
   */
  hideParentRows?: boolean;
  onCardClick: (task: TaskCardData) => void;
  onFlip?: (task: TaskCardData) => void;
  onCreateMilestone?: () => void;
  onEditMilestone?: (m: Milestone) => void;
  onDeleteMilestone?: (m: Milestone) => void;
}

interface KanbanSwimlaneRowProps {
  projectId: string;
  audience: 'internal' | 'external';
  canManageMilestones: boolean;
  /** null → synthetic "No milestone" row (or a subproject lane, see `lane`) */
  milestone: Milestone | null;
  /** MKS-419 — set → this row is a subproject lane (`milestone` is null). */
  lane?: SubprojectLane;
  rowKey: string;
  columns: BoardColumn[];
  /** quick 260726-dcz — collapsed flag per column, index-aligned with `columns`. */
  collapsedFlags: boolean[];
  /**
   * MKS-378 — render the cells without the row banner. Used for the "No milestone" row of a board
   * that has no milestones at all: the banner would label the only row, and with no banner there
   * is nothing to fold it with, so the row is never folded.
   */
  hideRowHeader?: boolean;
  byMilestoneId: Map<number, number>;
  unassignedCount: number;
  isFolded: boolean;
  onToggleFold: () => void;
  /** Quick 260914-huu — see KanbanSwimlaneProps.canReorderRows. */
  canReorderRows?: boolean;
  onEditMilestone: () => void;
  onDeleteMilestone: () => void;
  onCardClick: (task: TaskCardData) => void;
  onFlip?: (task: TaskCardData) => void;
  /** Phase 41 Plan 41-08 — forwarded to each TaskCard (Surface 5d-A). */
  projectTaskTypeId?: number | null;
  /** 260913-jpy — forwarded to each TaskCard. */
  stalenessConfig?: TaskStalenessConfig;
  /** MKS-184 — see KanbanSwimlaneCellProps.fillHeight. */
  fillHeight?: boolean;
  /** MKS-419 — see KanbanSwimlaneProps.subprojectsById. */
  subprojectsById?: ReadonlyMap<number, BoardSubproject>;
  /** MKS-500 — the board has no 240px label track, so the cells need no column-1 spacer. */
  noLabelTrack?: boolean;
}

interface KanbanSwimlaneCellProps {
  projectId: string;
  milestoneId: number | null;
  // Phase 51 INTAKE-02 (CF-2) — relaxed to number | null; the synthetic Triage
  // column has taskStateId=null (no real task_state row).
  taskStateId: number | null;
  columnName: string;
  /** Petra UAT 2026-05-19 — column hex color forwarded to TaskCard accent border. */
  columnColor: string;
  /** 260723-tst — column terminal flag forwarded to TaskCard for 💩 suppression. */
  columnIsTerminal: boolean;
  milestoneName: string;
  audience: 'internal' | 'external';
  tasks: TaskCardData[];
  onCardClick: (task: TaskCardData) => void;
  onFlip?: (task: TaskCardData) => void;
  /** Phase 41 Plan 41-08 — forwarded to each TaskCard (Surface 5d-A). */
  projectTaskTypeId?: number | null;
  /** 260913-jpy — forwarded to each TaskCard. */
  stalenessConfig?: TaskStalenessConfig;
  /**
   * MKS-184 — true only on a board with no milestones, where the single
   * swimlane row is sized by the grid (`minmax(0, 1fr)` track) rather than by
   * its cards. The cell then takes the whole track and scrolls inside it.
   * False on a milestone board: the cell keeps its 120px floor and is capped
   * at roughly one viewport so one 655-card column can no longer set the
   * height of the page.
   */
  fillHeight?: boolean;
  /** MKS-419 — see KanbanSwimlaneProps.subprojectsById. */
  subprojectsById?: ReadonlyMap<number, BoardSubproject>;
  /**
   * MKS-419 — merged board: the projects that contribute a state to this column.
   * The inline add form is offered only where the PARENT (`projectId`) has the
   * column, because a new task is always created in the parent.
   */
  columnProjectIds?: number[];
  /**
   * MKS-419 — set on a subproject lane's cells: the lane's project. It keys the
   * droppable, rides in the drop data (`rowProjectId`) so the page can refuse a drop
   * that would change the card's project, and suppresses the inline add form.
   */
  laneProjectId?: number;
}

interface ColumnHeaderCellProps {
  column: BoardColumn;
  canReorderColumns: boolean;
  columnIndex: number;
  /** quick 260726-dcz — whether this column is currently collapsed. */
  isColumnCollapsed: boolean;
  /** quick 260726-dcz — collapse/expand this column (available to all viewers). */
  onToggleColumnCollapse: () => void;
  /** MKS-378 — slim header mode: colour dot, name and count only, on one short line. */
  slim?: boolean;
  /** Quick task 260610-kv6 — show rename / recolor / delete affordances. */
  canManageStates?: boolean;
  onRename?: (stateId: number, newName: string) => void | Promise<void>;
  onRecolor?: (stateId: number, hex: string) => void | Promise<void>;
  onDelete?: (stateId: number) => void | Promise<void>;
  /** 260723-tst — flip the column's terminal flag (manager-gated). */
  onToggleTerminal?: (stateId: number, next: boolean) => void | Promise<void>;
  /** 260726-wip — flip the column's "work in progress" flag (manager-gated). */
  onToggleInProgress?: (stateId: number, next: boolean) => void | Promise<void>;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function tasksForCell(
  column: BoardColumn,
  milestoneId: number | null,
): TaskCardData[] {
  return column.tasks.filter((t) => t.milestoneId === milestoneId);
}

// MKS-487 — one shared empty list, so "nothing revealed" keeps a stable
// identity across renders and the memos below don't recompute for free.
const NO_REVEALED_COLUMNS: string[] = [];

const noopFlip = (_task: TaskCardData) => {
  // intentional no-op — onFlip is optional
};

// ---------------------------------------------------------------------------
// ColumnHeaderCell — donor: KanbanColumn.tsx:103-118
// ---------------------------------------------------------------------------

function ColumnHeaderCell({
  column,
  canReorderColumns,
  columnIndex,
  isColumnCollapsed,
  onToggleColumnCollapse,
  slim = false,
  canManageStates = false,
  onRename,
  onRecolor,
  onDelete,
  onToggleTerminal,
  onToggleInProgress,
}: ColumnHeaderCellProps) {
  const {
    setNodeRef,
    attributes,
    listeners,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: `col-${column.taskStateId}`,
    data: { type: 'column' as const, stateId: column.taskStateId },
    disabled: !canReorderColumns,
  });

  // Quick task 260610-kv6 — inline rename / recolor / delete UX, gated by
  // canManageStates. State is local to the header so the surrounding grid
  // doesn't re-render on every keystroke. Optimistic mutations live one
  // level up in KanbanBoardPage (mirrors ProjectFormPage handleRename /
  // handleRecolor / handleDelete).
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameDraft, setRenameDraft] = useState(column.name);
  const [isPickerOpen, setIsPickerOpen] = useState(false);

  // WR-07 — column drag is now wired to a visible Bars3 handle (instead of
  // the whole header). The handle owns the `useSortable` listeners; the
  // rest of the header is free for click targets (name → rename, swatch →
  // recolor, X → delete). Cells beneath each column remain independent
  // `useDroppable` zones (one per (milestoneId, taskStateId) cell) and stay
  // put while the header glides — Linear-style horizontal dragOverlay is
  // still deferred to Plan-32; current behavior is functionally correct
  // (drop fires, columns reorder on next render) and 0.6 opacity on the
  // header signals the drag in progress.
  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  // Phase 41 Plan 41-17 (Task 1) — v7 token recolor (D-Kanban-recolor).
  const containerClass = [
    'relative flex items-center gap-2 px-2 py-2',
    'border border-ms-border bg-ms-surface rounded-lg',
  ].join(' ');

  function commitRename() {
    const trimmed = renameDraft.trim();
    if (!trimmed || trimmed === column.name || column.taskStateId === null) {
      setRenameDraft(column.name);
      setIsRenaming(false);
      return;
    }
    void onRename?.(column.taskStateId, trimmed);
    setIsRenaming(false);
  }

  function cancelRename() {
    setRenameDraft(column.name);
    setIsRenaming(false);
  }

  function handleRenameKey(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitRename();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancelRename();
    }
  }

  // Synthetic Triage column carries taskStateId=null (Phase 51 INTAKE-02).
  // Edit affordances key off the concrete numeric id, so when it's null we
  // suppress them entirely — Triage is never renameable / recolorable /
  // deletable / draggable.
  const hasStateId = typeof column.taskStateId === 'number';
  const showManage = canManageStates && hasStateId;

  // quick 260726-dcz — collapsed state renders a narrow vertical strip: the
  // whole strip is an "expand" button. Kept inside ColumnHeaderCell (rather than
  // a separate component) so useSortable's setNodeRef stays attached and the
  // horizontal SortableContext can still measure this column's track.
  if (isColumnCollapsed) {
    // MKS-378 — short collapsed header: the old one (vertical name, 96px minimum) set the height
    // of the whole grid row, so every expanded header stretched to ~90px. The name is shown
    // vertically in the column body instead (KanbanSwimlaneRow) and in the tooltip here.
    return (
      <div
        ref={setNodeRef}
        className={`relative flex items-center justify-center px-1 ${
          slim ? 'py-1' : 'py-1.5'
        } border border-ms-border bg-ms-surface rounded-lg overflow-hidden`}
        style={style}
        data-column-index={columnIndex}
      >
        <button
          type="button"
          onClick={onToggleColumnCollapse}
          aria-label={`Expand ${column.name} column`}
          aria-expanded={false}
          title={`${column.name} (${column.tasks.length}) — expand`}
          className="flex flex-col items-center gap-0.5 w-full focus:outline-none focus:ring-2 focus:ring-primary-500 rounded"
        >
          {!slim && (
            <ChevronDoubleLeftIcon className="h-3.5 w-3.5 rotate-180 text-ms-text-muted shrink-0" />
          )}
          <span className="flex items-center gap-1">
            <span
              className="inline-block h-2 w-2 rounded-full shrink-0"
              style={{ backgroundColor: column.color }}
              aria-hidden
            />
            <span className="text-[11px] font-medium text-ms-text-muted">
              {column.tasks.length}
            </span>
          </span>
        </button>
      </div>
    );
  }

  if (slim) {
    // MKS-378 — slim header: names stay, management controls wait for the full header.
    return (
      <div
        ref={setNodeRef}
        className="relative flex items-center gap-2 px-2 py-1 border border-ms-border bg-ms-surface rounded-lg"
        style={style}
        data-column-index={columnIndex}
      >
        <span
          className="shrink-0 inline-block h-2.5 w-2.5 rounded-full"
          style={{ backgroundColor: column.color }}
          aria-hidden
        />
        <span className="min-w-0 flex-1 text-sm font-medium text-ms-text truncate" title={column.name}>
          {column.name}
        </span>
        <span className="text-xs font-medium text-ms-text-muted">{column.tasks.length}</span>
        {/* The collapse toggle is a view control, not management, so it stays in the slim header
            too — without it an expanded column could only be collapsed from the full header. */}
        <button
          type="button"
          className="shrink-0 -mr-1 p-0.5 text-ms-text-muted hover:text-ms-text focus:outline-none focus:ring-2 focus:ring-primary-500 rounded"
          aria-label={`Collapse ${column.name} column`}
          aria-expanded={true}
          title={`Collapse ${column.name}`}
          onClick={onToggleColumnCollapse}
        >
          <ChevronDoubleLeftIcon className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  }

  return (
    <div
      ref={setNodeRef}
      className={containerClass}
      style={style}
      data-column-index={columnIndex}
    >
      {/* Drag handle — visible only when canReorderColumns. Owns the
          useSortable listeners so click targets to its right (swatch /
          name / delete) don't compete with drag activation. */}
      {canReorderColumns && hasStateId && (
        <button
          type="button"
          className="shrink-0 -ml-1 p-1 text-ms-text-muted hover:text-ms-text cursor-grab active:cursor-grabbing select-none"
          aria-label={`Reorder ${column.name} column`}
          {...attributes}
          {...listeners}
        >
          <Bars3Icon className="h-4 w-4" />
        </button>
      )}

      {/* Color swatch — clickable when canManageStates → opens palette popover. */}
      {showManage ? (
        <button
          type="button"
          className="shrink-0 inline-block h-3 w-3 rounded-full focus:outline-none focus:ring-2 focus:ring-primary-500"
          style={{ backgroundColor: column.color }}
          aria-label={`Change ${column.name} color`}
          onClick={() => setIsPickerOpen((v) => !v)}
        />
      ) : (
        <span
          className="shrink-0 inline-block h-3 w-3 rounded-full"
          style={{ backgroundColor: column.color }}
          aria-hidden
        />
      )}
      {isPickerOpen && (
        <StateColorPickerPopover
          currentColor={column.color}
          onPick={(hex) => {
            if (column.taskStateId !== null) {
              void onRecolor?.(column.taskStateId, hex);
            }
            setIsPickerOpen(false);
          }}
          onClose={() => setIsPickerOpen(false)}
          className="absolute top-full left-0 mt-1 z-30"
        />
      )}

      {/* Name — click to rename when canManageStates. */}
      {isRenaming ? (
        <input
          type="text"
          value={renameDraft}
          onChange={(e) => setRenameDraft(e.target.value)}
          onKeyDown={handleRenameKey}
          onBlur={commitRename}
          autoFocus
          aria-label={`Rename ${column.name} column`}
          className="min-w-0 flex-1 text-sm font-medium text-ms-text bg-transparent border-b border-primary-500 focus:outline-none"
        />
      ) : showManage ? (
        <button
          type="button"
          className="min-w-0 flex-1 text-left text-sm font-medium text-ms-text truncate hover:underline"
          onClick={() => {
            setRenameDraft(column.name);
            setIsRenaming(true);
          }}
        >
          {column.name}
        </button>
      ) : (
        <span className="min-w-0 flex-1 text-sm font-medium text-ms-text truncate">
          {column.name}
        </span>
      )}

      {/* 260726-wip — work-in-progress marker + toggle. When manageable, a
          clickable play flips the flag (solid blue = WIP, gray outline = not).
          Otherwise a solid blue play is shown only for WIP columns as a
          read-only indicator. Mutually exclusive with the terminal flag. */}
      {showManage ? (
        <button
          type="button"
          aria-label={
            column.isInProgress
              ? `Unmark ${column.name} as work in progress`
              : `Mark ${column.name} as work in progress`
          }
          aria-pressed={column.isInProgress}
          title={
            column.isInProgress
              ? 'Work-in-progress state (task actively being worked) — click to unmark'
              : 'Mark as work-in-progress state (task actively being worked)'
          }
          className={`shrink-0 rounded focus:outline-none focus:ring-2 focus:ring-primary-500 ${
            column.isInProgress
              ? 'text-blue-600'
              : 'text-ms-text-muted/50 hover:text-ms-text-muted'
          }`}
          onClick={() => {
            if (column.taskStateId !== null) {
              void onToggleInProgress?.(column.taskStateId, !column.isInProgress);
            }
          }}
        >
          {column.isInProgress ? (
            <PlayCircleSolidIcon className="h-4 w-4" />
          ) : (
            <PlayCircleIcon className="h-4 w-4" />
          )}
        </button>
      ) : column.isInProgress ? (
        <span
          role="img"
          aria-label="Work-in-progress state"
          title="Work-in-progress state (task actively being worked)"
          className="shrink-0 text-blue-600"
        >
          <PlayCircleSolidIcon className="h-4 w-4" />
        </span>
      ) : null}

      {/* 260723-tst — terminal marker + toggle. When manageable, a clickable
          check flips the flag (solid green = final, gray outline = not).
          Otherwise a solid green check is shown only for terminal columns as a
          read-only "final state" indicator. */}
      {showManage ? (
        <button
          type="button"
          aria-label={
            column.isTerminal
              ? `Unmark ${column.name} as final`
              : `Mark ${column.name} as final`
          }
          aria-pressed={column.isTerminal}
          title={
            column.isTerminal
              ? 'Final state (task no longer in progress) — click to unmark'
              : 'Mark as final state (task no longer in progress)'
          }
          className={`shrink-0 rounded focus:outline-none focus:ring-2 focus:ring-primary-500 ${
            column.isTerminal
              ? 'text-green-600'
              : 'text-ms-text-muted/50 hover:text-ms-text-muted'
          }`}
          onClick={() => {
            if (column.taskStateId !== null) {
              void onToggleTerminal?.(column.taskStateId, !column.isTerminal);
            }
          }}
        >
          {column.isTerminal ? (
            <CheckCircleSolidIcon className="h-4 w-4" />
          ) : (
            <CheckCircleIcon className="h-4 w-4" />
          )}
        </button>
      ) : column.isTerminal ? (
        <span
          role="img"
          aria-label="Final state"
          title="Final state (task no longer in progress)"
          className="shrink-0 text-green-600"
        >
          <CheckCircleSolidIcon className="h-4 w-4" />
        </span>
      ) : null}

      <span className="text-xs font-medium text-ms-text-muted">
        {column.tasks.length}
      </span>

      {/* quick 260726-dcz — collapse toggle. Available to every viewer (not
          gated by canManageStates); collapses this column to a narrow strip. */}
      <button
        type="button"
        className="shrink-0 p-1 text-ms-text-muted hover:text-ms-text focus:outline-none focus:ring-2 focus:ring-primary-500 rounded"
        aria-label={`Collapse ${column.name} column`}
        aria-expanded={true}
        title={`Collapse ${column.name}`}
        onClick={onToggleColumnCollapse}
      >
        <ChevronDoubleLeftIcon className="h-4 w-4" />
      </button>

      {/* Delete X — gated by canManageStates. Page-level handler renders
          the STATE_IN_USE conflict banner on rejection. */}
      {showManage && (
        <button
          type="button"
          className="shrink-0 p-1 text-ms-text-muted hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 rounded"
          aria-label={`Delete ${column.name} column`}
          onClick={() => {
            if (column.taskStateId !== null) {
              void onDelete?.(column.taskStateId);
            }
          }}
        >
          <XMarkIcon className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// HiddenColumnsStrip — MKS-487
// ---------------------------------------------------------------------------

interface HiddenColumnsStripProps {
  columns: BoardColumn[];
  onReveal: (columnKey: string) => void;
  onRevealAll: () => void;
}

/**
 * MKS-487 — the trailing strip that stands in for the columns the empty rule
 * took out of the grid: a count, then one chip per hidden column (state dot +
 * name) that puts that column back, plus "Show all". Revealing is view state
 * for this board render only — nothing is written to preferences, so the
 * column slips back out of the grid as soon as it is empty again on a later
 * visit (an explicit collapse/expand is what persists, see useColumnCollapse).
 *
 * It lives in the header band only (like the AddStateColumn slot next to it):
 * the body rows render no cell for its track.
 */
function HiddenColumnsStrip({ columns, onReveal, onRevealAll }: HiddenColumnsStripProps) {
  return (
    <div
      data-testid="kanban-hidden-columns"
      className="flex flex-col gap-1 px-2 py-2 border border-dashed border-ms-border bg-ms-surface rounded-lg max-h-[220px] overflow-y-auto"
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-medium text-ms-text-muted">
          {columns.length} hidden
        </span>
        <button
          type="button"
          onClick={onRevealAll}
          className="shrink-0 text-xs font-medium text-primary-600 hover:underline focus:outline-none focus:ring-2 focus:ring-primary-500 rounded"
        >
          Show all
        </button>
      </div>
      <div className="flex flex-wrap gap-1">
        {columns.map((column) => (
          <button
            key={column.taskStateId}
            type="button"
            onClick={() => onReveal(String(column.taskStateId))}
            aria-label={`Show ${column.name} column`}
            title={`${column.name} — show`}
            className="max-w-full inline-flex items-center gap-1 px-1.5 py-0.5 border border-ms-border rounded text-[11px] font-medium text-ms-text-muted hover:text-ms-text hover:bg-ms-canvas focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <span
              className="inline-block h-2 w-2 rounded-full shrink-0"
              style={{ backgroundColor: column.color }}
              aria-hidden
            />
            <span className="truncate">{column.name}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// KanbanSwimlaneCell — donor: KanbanColumn.tsx:58-93 (cell drop target)
// ---------------------------------------------------------------------------

function KanbanSwimlaneCell({
  projectId,
  milestoneId,
  taskStateId,
  columnName,
  columnColor,
  columnIsTerminal,
  milestoneName,
  audience,
  tasks,
  onCardClick,
  onFlip,
  projectTaskTypeId,
  stalenessConfig,
  fillHeight = false,
  subprojectsById,
  columnProjectIds,
  laneProjectId,
}: KanbanSwimlaneCellProps) {
  // CR-01 fix — the cell drop target gates ONLY on audience. The legacy
  // KanbanColumn allowed any internal actor (admin / PM / project assignee
  // per setTaskState in taskMutationService) to drag a task between states.
  // Tying the per-cell droppable to canManageMilestones (admin OR PM only)
  // silently regressed assignees' ability to move tasks. The page-level
  // `handleDragEnd` decides which mutations to fire, and the server-side
  // gate on the milestone PATCH (admin/PM-only) is the source of truth for
  // the milestone-change permission. canManageMilestones still gates the
  // milestone-management UI (edit/delete row icons, "+ New milestone") in
  // KanbanSwimlaneRow.
  const disabled = audience !== 'internal';
  const rowIdPart = laneProjectId !== undefined ? subprojectLaneKey(laneProjectId) : milestoneId ?? 'none';
  const { setNodeRef, isOver } = useDroppable({
    id: `swimlane-cell-${rowIdPart}-${taskStateId}`,
    data: {
      type: 'swimlane-cell' as const,
      milestoneId,
      taskStateId,
      ...(laneProjectId !== undefined ? { rowProjectId: laneProjectId } : {}),
    },
    disabled,
  });

  // Phase 41 Plan 41-17 (Task 1) — v7 token recolor (D-Kanban-recolor):
  // border-gray-200 → border-ms-border, bg-gray-50 → bg-ms-canvas. The
  // primary-50 / primary-400 ring on drop-over already maps to the v7 orange
  // ramp via the 41-01 tailwind bridge — no change needed.
  //
  // MKS-184 — the cell is the scroll container for its own column. Before
  // this the only scroller on the board was the page-level one in
  // KanbanBoardPage, so the tallest column (655 cards on project 794) set the
  // height of every other column's empty space.
  //
  //   fillHeight  — board with no milestones: the single body row is a
  //                 `minmax(0, 1fr)` grid track sized by the viewport, so the
  //                 cell takes the whole track (`h-full`) and scrolls inside
  //                 it. `overscroll-contain` keeps a bottomed-out column from
  //                 chaining its wheel into the board container, which has
  //                 nothing left to scroll vertically here anyway.
  //   otherwise   — board with milestones (variant (a) of the task): cap the
  //                 cell at roughly one viewport of board (layout chrome 4rem
  //                 + page header 4rem + the container's p-4 + the header band
  //                 + the milestone row banner ≈ 16rem) and keep the 120px
  //                 floor so an empty cell is still a visible drop target. NO
  //                 overscroll-contain here: the page still scrolls through
  //                 the milestone rows and the wheel MUST chain into it once
  //                 the cell bottoms out, or rows below become unreachable
  //                 while the pointer rests on a long column.
  const cellClass = [
    'p-2 overflow-y-auto',
    fillHeight ? 'h-full min-h-0 overscroll-contain' : 'min-h-[120px] max-h-[calc(100vh-16rem)]',
    'border-r border-ms-border last:border-r-0',
    'bg-ms-canvas transition-colors rounded-none',
    isOver && !disabled
      ? 'bg-primary-50 ring-2 ring-inset ring-primary-400'
      : '',
  ]
    .filter(Boolean)
    .join(' ');

  // MKS-184 — WHICH of the two nodes carries `setNodeRef` decides whether
  // dnd-kit can auto-scroll this column while a card is dragged over its edge.
  // `useAutoScroller` only ever scrolls `getScrollableAncestors(overNode)`,
  // and that helper skips the over node ITSELF (core.esm.js:713,
  // `if (node !== element)`), so a droppable that *is* the scroller never
  // auto-scrolls — the scroller has to be an ANCESTOR of the droppable node.
  //
  //   fillHeight  — droppable is the inner content node, so the scroller is its
  //                 ancestor and dnd-kit scrolls the column at the edges (the
  //                 board container stays in the list too and keeps handling
  //                 the horizontal edges). `min-h-full` keeps the droppable
  //                 covering the whole visible cell, so an almost-empty column
  //                 still accepts a drop anywhere in its empty space.
  //   otherwise   — droppable stays the (clipped) scroller box. Measuring the
  //                 inner node here would hand dnd-kit a rect as tall as the
  //                 column's CONTENT — 655 cards ≈ 65,000px — which, now that
  //                 the visible box is capped, would cover every cell of every
  //                 milestone row below it and make `pointerWithin` return the
  //                 wrong cell. Drops into a scrolled column still work (the
  //                 whole cell is one target); only the drag-time scroll of the
  //                 column itself is given up, and the page scroll that variant
  //                 (a) keeps is still auto-scrolled by dnd-kit.
  return (
    <div
      ref={fillHeight ? undefined : setNodeRef}
      role="region"
      aria-label={`${columnName} for ${milestoneName}`}
      data-testid={`swimlane-cell-${rowIdPart}-${taskStateId}`}
      className={cellClass}
    >
      <div
        ref={fillHeight ? setNodeRef : undefined}
        className={fillHeight ? 'space-y-2 min-h-full' : 'space-y-2'}
      >
        {tasks.map((task) => {
          // MKS-419 — a subproject card carries its slug badge and NOT the
          // parent's SLA-bar / staleness context (those belong to the parent).
          const sub = subprojectsById?.get(task.projectId);
          return (
            <TaskCard
              key={task.id}
              task={task}
              audience={audience}
              canDrag={audience === 'internal'}
              projectTaskTypeId={sub ? null : projectTaskTypeId}
              stalenessConfig={sub ? undefined : stalenessConfig}
              accentColor={columnColor}
              isTerminal={columnIsTerminal}
              subprojectBadge={sub ? { slug: sub.slug, name: sub.name } : undefined}
              onClick={onCardClick}
              onFlip={onFlip ?? noopFlip}
            />
          );
        })}
        {/* AddTaskInlineForm renders nothing for external audience (line 47 of
         * AddTaskInlineForm.tsx) — no extra check needed here.
         * Phase 51 INTAKE-02 — skip for the synthetic Triage column (taskStateId=null);
         * triage tasks arrive via external create only, not inline add. */}
        {taskStateId !== null &&
          laneProjectId === undefined &&
          (columnProjectIds === undefined || columnProjectIds.includes(Number(projectId))) && (
          <AddTaskInlineForm
            projectId={projectId}
            taskStateId={taskStateId}
            milestoneId={milestoneId}
            audience={audience}
            projectTaskTypeId={projectTaskTypeId}
          />
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// KanbanSwimlaneRow — donor: MilestoneSidebar.tsx:200-281 (MilestoneRow)
// ---------------------------------------------------------------------------

function KanbanSwimlaneRow({
  projectId,
  audience,
  canManageMilestones,
  milestone,
  lane,
  rowKey,
  columns,
  collapsedFlags,
  hideRowHeader = false,
  byMilestoneId,
  unassignedCount,
  isFolded,
  onToggleFold,
  canReorderRows = false,
  onEditMilestone,
  onDeleteMilestone,
  onCardClick,
  onFlip,
  projectTaskTypeId,
  stalenessConfig,
  fillHeight = false,
  subprojectsById,
  noLabelTrack = false,
}: KanbanSwimlaneRowProps) {
  const isNoMilestoneRow = milestone === null;
  const displayName = lane ? lane.title : isNoMilestoneRow ? 'No milestone' : milestone.name;
  const count = lane
    ? lane.count
    : isNoMilestoneRow
      ? unassignedCount
      : byMilestoneId.get(milestone.id) ?? 0;
  const dateRange = isNoMilestoneRow
    ? null
    : formatDateRange(milestone.startDate, milestone.endDate);

  // Quick 260914-huu — vertical row-reorder sortable. Called unconditionally
  // (hook rules) even for the synthetic "No milestone" row; `disabled: true`
  // there means the row can neither be dragged NOR dropped onto, which is
  // exactly what pins it last (it stays in `items` below so the context's
  // membership matches what renders).
  const {
    setNodeRef: setRowNodeRef,
    attributes: rowAttributes,
    listeners: rowListeners,
    isDragging: isRowDragging,
    isOver: isRowOver,
  } = useSortable({
    id: `row-${rowKey}`,
    data: {
      type: 'milestone-row' as const,
      milestoneId: isNoMilestoneRow ? null : milestone.id,
    },
    disabled: isNoMilestoneRow || !canReorderRows,
  });

  // Pitfall 5: <div role="button"> (NOT <button>) so nested <button> icons are
  // not invalid HTML. Enter / Space activate the toggle (UI-SPEC §3).
  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onToggleFold();
    }
  };

  // Phase 41 Plan 41-17 (Task 1) — v7 token recolor (D-Kanban-recolor):
  // border-gray-300 → border-ms-border-strong, bg-gray-200 → bg-ms-surface-2,
  // hover:bg-gray-300 → hover:bg-ms-border, text-gray-700 → text-ms-text.
  // Locked Phase 31 rule (UI-SPEC line 332) preserved: no active accent —
  // every row keeps border-l-2 border-transparent for box-sizing baseline.
  const headerClass = [
    'w-full flex items-center gap-2 px-3 py-3 min-h-[40px]',
    'border-l-2 border-transparent transition-colors text-left group',
    'border-y border-ms-border-strong',
    'focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-400',
    'text-ms-text bg-ms-surface-2 hover:bg-ms-border cursor-pointer select-none',
    // Quick 260914-huu — drop-target indicator. Functionally correct drop,
    // richer preview deferred (mirrors the column header's own documented
    // stance) — no sortable transform is applied to the row header (design
    // section: swimlane rows are non-contiguous sortable nodes, so a list
    // strategy's computed translate would be wrong for them).
    isRowOver && !isRowDragging ? 'border-t-2 border-t-primary-500' : '',
  ].join(' ');

  const headerStyle: CSSProperties = {
    gridColumn: '1 / -1',
    opacity: isRowDragging ? 0.6 : 1,
  };

  const rowNoun = lane ? 'subproject' : 'milestone';
  const ariaLabel = isFolded
    ? `Expand ${displayName} ${rowNoun}`
    : `Collapse ${displayName} ${rowNoun}`;

  const header = (
    <div
      ref={setRowNodeRef}
      role="button"
      tabIndex={0}
      aria-expanded={!isFolded}
      aria-label={ariaLabel}
      onClick={onToggleFold}
      onKeyDown={handleKeyDown}
      className={headerClass}
      style={headerStyle}
      data-testid={lane ? `subproject-lane-header-${lane.id}` : undefined}
    >
      {canReorderRows && !isNoMilestoneRow && (
        <button
          type="button"
          className="shrink-0 -ml-1 p-1 text-ms-text-muted hover:text-ms-text cursor-grab active:cursor-grabbing select-none opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
          aria-label={`Reorder ${displayName} milestone`}
          onClick={(e) => {
            // Pitfall 6: the parent header is role="button" whose click
            // toggles the fold — without this the handle's drag activation
            // would also fold the row.
            e.stopPropagation();
          }}
          onKeyDown={(e) => {
            e.stopPropagation();
          }}
          {...rowAttributes}
          {...rowListeners}
        >
          <Bars3Icon className="h-4 w-4" />
        </button>
      )}
      <ChevronDownIcon
        className={`w-4 h-4 text-ms-text-secondary shrink-0 transition-transform duration-150 ease-out ${
          isFolded ? '-rotate-90' : ''
        }`}
        aria-hidden
      />
      <div className="flex flex-col min-w-0 flex-1">
        <span className="text-sm font-medium truncate" title={lane ? displayName : undefined}>
          {displayName}
        </span>
        {lane && (
          // MKS-419 — the subproject's slug, same neutral chip as the card badge.
          <span
            data-testid="subproject-lane-slug"
            className="self-start rounded bg-ms-canvas px-1.5 text-[11px] font-medium text-ms-text-secondary"
          >
            {lane.slug}
          </span>
        )}
        {dateRange && (
          <span className="text-xs font-medium text-ms-text-secondary truncate">
            {dateRange}
          </span>
        )}
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {!isNoMilestoneRow && milestone.status === 'planned' && (
          // 260913-l8n / MKS-174 — outlined (not filled) neutral pill: the
          // theme exposes no blue/slate token (only ms-* neutrals), so
          // Planned is distinguished from Completed by outline vs fill
          // rather than by hue.
          <span className="border border-ms-border-strong text-ms-text-secondary rounded-full px-2 py-0.5 text-xs font-medium">
            Planned
          </span>
        )}
        {!isNoMilestoneRow && milestone.status === 'completed' && (
          <span className="bg-ms-canvas text-ms-text-secondary rounded-full px-2 py-0.5 text-xs font-medium">
            Completed
          </span>
        )}
        <span className="bg-ms-canvas text-ms-text rounded-full px-2 py-0.5 text-xs font-medium">
          {count}
        </span>
        {!isNoMilestoneRow && canManageMilestones && (
          <span className="flex items-center gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
            <button
              type="button"
              aria-label={`Edit milestone ${milestone.name}`}
              title="Edit milestone"
              onClick={(e) => {
                // Pitfall 6: do NOT bubble into the row-header fold toggle.
                e.stopPropagation();
                onEditMilestone();
              }}
              className="p-0.5 text-ms-text-muted hover:text-ms-text focus:outline-none focus:ring-2 focus:ring-primary-400 focus:rounded"
            >
              <PencilSquareIcon className="w-4 h-4" />
            </button>
            <button
              type="button"
              aria-label={`Delete milestone ${milestone.name}`}
              title="Delete milestone"
              onClick={(e) => {
                // Pitfall 6: do NOT bubble into the row-header fold toggle.
                e.stopPropagation();
                onDeleteMilestone();
              }}
              className="p-0.5 text-ms-text-muted hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:rounded"
            >
              <TrashIcon className="w-4 h-4" />
            </button>
          </span>
        )}
      </div>
    </div>
  );

  if (isFolded && !hideRowHeader) {
    // CONTEXT D-17: when folded, ONLY the row header renders. Cells are absent
    // from the DOM (test GOAL-08 asserts no swimlane-cell-{id}-* nodes exist).
    return <>{header}</>;
  }

  return (
    <>
      {!hideRowHeader && header}
      {/* Spacer aligns cells with column-header strip (cols 2..N+1) now that
       * the row header banner spans the full grid width above. */}
      {!noLabelTrack && <div aria-hidden style={{ gridColumn: '1' }} />}
      {columns.map((column, i) =>
        collapsedFlags[i] ? (
          // quick 260726-dcz — collapsed column: inert narrow placeholder that
          // fills the 44px grid track (no droppable, no cards) so rows stay
          // aligned with the collapsed header above.
          <div
            key={`cell-${rowKey}-${column.taskStateId}`}
            aria-hidden
            className="bg-ms-canvas border-r border-ms-border last:border-r-0"
          >
            {/* MKS-378 — the collapsed column's name, moved here out of its (now short) header. */}
            <div className="flex justify-center pt-2">
              <span className="[writing-mode:vertical-rl] text-xs font-medium text-ms-text-muted truncate max-h-[180px]">
                {column.name}
              </span>
            </div>
          </div>
        ) : (
          <KanbanSwimlaneCell
            key={`cell-${rowKey}-${column.taskStateId}`}
            projectId={projectId}
            milestoneId={isNoMilestoneRow ? null : milestone.id}
            laneProjectId={lane?.id}
            taskStateId={column.taskStateId}
            columnName={column.name}
            columnColor={column.color}
            columnIsTerminal={column.isTerminal ?? false}
            milestoneName={displayName}
            audience={audience}
            tasks={
              lane
                ? column.tasks.filter((t) => t.projectId === lane.id)
                : tasksForCell(column, isNoMilestoneRow ? null : milestone.id)
            }
            onCardClick={onCardClick}
            onFlip={onFlip}
            projectTaskTypeId={projectTaskTypeId}
            stalenessConfig={stalenessConfig}
            fillHeight={fillHeight}
            subprojectsById={subprojectsById}
            columnProjectIds={column.projectIds}
          />
        ),
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// KanbanSwimlane — root layout
// ---------------------------------------------------------------------------

export function KanbanSwimlane({
  projectId,
  boardData,
  audience,
  canManageMilestones,
  canReorderColumns = false,
  canManageStates = false,
  canReorderRows = false,
  onRenameState,
  onRecolorState,
  onDeleteState,
  onToggleTerminalState,
  onToggleInProgressState,
  trailingHeaderSlot,
  // isDraggingFromState is reserved for future row-level drag treatment;
  // Phase 31 keeps the signal on the target cell only (CONTEXT D-02).
  isDraggingFromState: _isDraggingFromState,
  isDraggingCard = false,
  fillViewportHeight = false,
  onCardClick,
  onFlip,
  onCreateMilestone: _onCreateMilestone,
  onEditMilestone,
  onDeleteMilestone,
  projectTaskTypeId,
  stalenessConfig,
  subprojectsById,
  subprojectLanes,
  hideParentRows = false,
  slimHeader: slimHeaderProp,
}: KanbanSwimlaneProps) {
  // external-board-redirect-login (2026-07-02) — MUST pass `audience` so
  // external viewers use the raw-fetch transport (externalMilestones.ts),
  // never the internal client.ts one. See useMilestones.ts header comment.
  const milestonesQuery = useMilestones(projectId, audience);
  const { isFolded, onToggleFold } = useFoldState(projectId);
  // quick 260726-dcz — per-column collapse (default: terminal/"Done" collapsed).
  const { isCollapsed, onToggleCollapse, hasStoredPreference } = useColumnCollapse(projectId);
  // MKS-487 — columns the viewer has clicked back into the grid from the
  // trailing strip. View state only (never stored), owned by ONE project: the
  // project id is kept next to the keys and a mismatch reads as empty, so the
  // reveal resets when the board moves to another project.
  //
  // Derived during render rather than cleared from a `useEffect`: the extra
  // post-mount render a reset effect costs re-registers every swimlane cell's
  // droppable, and it also swallowed the board's optimistic milestone-row
  // reorder (KanbanSwimlaneReorder's "reorders to [2,3,1]" assertion went red
  // with the effect in place and green without it).
  const [revealed, setRevealed] = useState<{ projectId: string; keys: string[] }>({
    projectId,
    keys: NO_REVEALED_COLUMNS,
  });
  const revealedColumnKeys =
    revealed.projectId === projectId ? revealed.keys : NO_REVEALED_COLUMNS;
  // MKS-378 — the slim/full header choice (per browser). MKS-500: when the page
  // passes `slimHeader` the toolbar owns both the choice and the control, and
  // this instance's own state is unused (the hook still runs — hooks are not
  // conditional — it simply never reaches the header band).
  const { slimHeader: ownSlimHeader, toggleSlimHeader } = useSlimHeaderPreference();
  const headerToggleInToolbar = slimHeaderProp !== undefined;
  const slimHeader = slimHeaderProp ?? ownSlimHeader;
  // quick 260914-huu — private per-user overlay ordering of milestone rows.
  // This component holds its own instance of the hook purely to read the
  // overlay (orderMilestones); KanbanBoardPage holds a second instance to
  // call onReorder. Both read the same ['preferences'] cache through the
  // same pure function, so they cannot diverge.
  const { orderMilestones } = useMilestoneOrder(projectId);

  // MKS-419 — with subproject lanes the parent's rows hold ONLY the parent's cards
  // (a subproject card lives in its own lane), and the milestone counts follow. The
  // column header counts keep reading `boardData`: they are the whole merged column.
  const lanes = subprojectLanes ?? [];
  const hasLanes = lanes.length > 0;
  const parentColumns = useMemo(() => {
    if (subprojectLanes === undefined) return boardData.columns;
    const parentId = Number(projectId);
    return boardData.columns.map((c) => ({
      ...c,
      tasks: c.tasks.filter((t) => t.projectId === parentId),
    }));
  }, [boardData.columns, subprojectLanes, projectId]);
  // "Done" is excluded from the count as the highest-ordering column; on the merged
  // board that is the highest among the columns the PARENT has (a subproject-only
  // column is appended after them).
  const counts = computeMilestoneCounts(
    subprojectLanes === undefined
      ? boardData
      : {
          ...boardData,
          columns: parentColumns.filter(
            (c) => c.projectIds === undefined || c.projectIds.includes(Number(projectId)),
          ),
        },
  );
  // `?? []` allocates a new array reference every render, which would make
  // the useMemo below re-run on every render if used directly as a
  // dependency — memoize it on the one thing that actually changes.
  const milestones = useMemo(() => milestonesQuery.data ?? [], [milestonesQuery.data]);
  const orderedMilestones = useMemo(
    () => orderMilestones(milestones),
    [orderMilestones, milestones],
  );

  // Quick 260914-ims — a reorder needs at least 2 real milestones to have a
  // second position to drop into; the synthetic "No milestone" row is pinned
  // last by its own `disabled` flag and never part of this count. Gated here
  // (call-site) rather than inside KanbanSwimlaneRow, which doesn't know the
  // list length. This same value also feeds the row's `useSortable` disabled
  // flag (via canReorderRows), so a single-milestone board loses the live
  // sortable node too — no invisible drag target left behind.
  const rowsReorderable = canReorderRows && orderedMilestones.length >= 2;

  // MKS-487 — which of boardData.columns leave the grid entirely, index-aligned
  // with boardData.columns. A column is empty on the board as RENDERED (the
  // filter bar and the search box empty a non-matching column rather than
  // dropping it, so this reacts to both), and:
  //   - a stored collapse preference wins in BOTH directions — an explicitly
  //     collapsed column keeps its 44px strip, an explicitly expanded one keeps
  //     its full track even while empty. Only a column with no stored entry at
  //     all is hidden (its default is `isTerminal`, which is a default, not a
  //     choice);
  //   - a column the viewer clicked out of the strip stays out of hiding;
  //   - during a card drag everything is revealed, because the collapsed/hidden
  //     cell is inert (no droppable) and an empty state would otherwise be
  //     unreachable by drag;
  //   - and the board never hides EVERY column: a project with no tasks, or a
  //     search that matches nothing, renders every column as it always did.
  const columnHidden = useMemo(() => {
    const none = boardData.columns.map(() => false);
    if (isDraggingCard) return none;
    const flags = boardData.columns.map((c) => {
      const key = String(c.taskStateId);
      return (
        c.tasks.length === 0 &&
        !hasStoredPreference(key) &&
        !revealedColumnKeys.includes(key)
      );
    });
    return flags.length > 0 && flags.every(Boolean) ? none : flags;
  }, [boardData.columns, isDraggingCard, hasStoredPreference, revealedColumnKeys]);

  // MKS-487 — everything below the header band renders the VISIBLE columns
  // only; `columnCollapsed`, the grid tracks and each row's cells all have to
  // stay index-aligned with this list, not with boardData.columns. Reorder,
  // the slide-over's status options and the list view keep reading
  // `data.columns`, so a hidden column keeps its server position and comes back
  // in place.
  const visibleColumns = useMemo(
    () => boardData.columns.filter((_c, i) => !columnHidden[i]),
    [boardData.columns, columnHidden],
  );
  const hiddenColumns = useMemo(
    () => boardData.columns.filter((_c, i) => columnHidden[i]),
    [boardData.columns, columnHidden],
  );
  // parentColumns is boardData.columns with the subproject cards filtered out —
  // same length and order, so the same mask applies.
  const visibleParentColumns = useMemo(
    () => parentColumns.filter((_c, i) => !columnHidden[i]),
    [parentColumns, columnHidden],
  );

  // quick 260726-dcz — collapsed flag per column, index-aligned with
  // the rendered (visible) columns. The default is column.isTerminal so Done
  // columns start collapsed; a stored explicit boolean (from useColumnCollapse)
  // overrides it.
  const columnCollapsed = visibleColumns.map((c) =>
    isCollapsed(String(c.taskStateId), c.isTerminal ?? false),
  );

  // Quick task 260610-kv6 — extend the grid by one when the trailing slot
  // (AddStateColumn) is mounted. Per-row cells still iterate boardData.columns
  // (real columns only), so the N+1 slot in each non-header row stays empty —
  // which is exactly the intended UX: the + Add chip sits next to the last
  // column header, with no body content below it.
  //
  // quick 260726-dcz — build an EXPLICIT gridTemplateColumns so collapsed
  // columns shrink to a 44px strip while expanded ones keep their own track.
  // (Replaces the former uniform `repeat(--col-count, minmax(320px,1fr))`.)
  // Row-header column stays 240px; the parent overflow-x-auto surfaces a
  // scrollbar once the tracks exceed the viewport.
  //
  // MKS-487 — a hidden column contributes NO track at all (it is absent from
  // this template, from the header band and from every row's cells); the
  // trailing strip that lists them takes one fixed 240px track of its own,
  // after the unchanged AddStateColumn slot.
  //
  // MKS-490 — dense board: expanded columns are a fixed 260px instead of
  // stretching to fill the screen, and the trailing "Add state" slot sizes to
  // its chip, so more columns fit side by side. Cards move their right-hand
  // cluster into a footer row (TaskCard) to keep titles readable at 260px.
  const expandedTrack = '260px';
  // MKS-500 — with the header toggle in the toolbar the 240px track holds nothing
  // (row banners span the full width), so it is dropped and column 1 starts at the edge.
  const gridTemplateColumns = [
    ...(headerToggleInToolbar ? [] : ['240px']),
    ...visibleColumns.map((_c, i) => (columnCollapsed[i] ? '44px' : expandedTrack)),
    ...(trailingHeaderSlot ? ['max-content'] : []),
    ...(hiddenColumns.length > 0 ? ['240px'] : []),
  ].join(' ');
  const gridStyle = { gridTemplateColumns } as CSSProperties;

  // MKS-184 — fill mode. Only a board whose milestone list has LOADED and is
  // EMPTY renders a single body row ("No milestone"), which is the one shape
  // the explicit gridTemplateRows below can describe. The page's own
  // `h-full` row wrapper is driven by the same condition on the same query, so
  // the two cannot disagree; re-deriving it here means a stale prop can at
  // worst cost the fill, never produce a mis-tracked grid.
  const fillHeight =
    fillViewportHeight &&
    !hasLanes &&
    !milestonesQuery.isLoading &&
    !milestonesQuery.isError &&
    orderedMilestones.length === 0;

  // Body-grid rows in fill mode, in render order: the PM-only "No milestones
  // yet" hint (same gate as its JSX below), the "No milestone" row banner, and
  // last the cells row — which takes the remaining space. `minmax(0, 1fr)` and
  // not `1fr`: a `1fr` track has an `auto` minimum, so the cell's own content
  // would still push the track (and the page) taller than the viewport.
  // A folded row renders no cells and simply leaves that last track empty.
  // MKS-378: a board without milestones shows neither the hint nor the row banner, so the cells
  // row is the only track.
  const hideEmptyMilestoneChrome =
    !milestonesQuery.isLoading &&
    !milestonesQuery.isError &&
    orderedMilestones.length === 0;
  const fillRowTracks = hideEmptyMilestoneChrome ? 0 : canManageMilestones ? 2 : 1;
  // MKS-419 — the parent's "No milestone" banner labels the only row of a board with
  // no milestones, so it is hidden there; next to subproject lanes it is one row of
  // several and keeps its banner.
  const hideParentRowBanner = hideEmptyMilestoneChrome && !hasLanes;
  const bodyGridStyle: CSSProperties = fillHeight
    ? {
        ...gridStyle,
        gridTemplateRows:
          fillRowTracks > 0 ? `repeat(${fillRowTracks}, auto) minmax(0, 1fr)` : 'minmax(0, 1fr)',
      }
    : gridStyle;

  return (
    <div className={fillHeight ? 'flex flex-col h-full min-h-0' : 'flex flex-col'}>
      {/*
       * Sticky header band (260911-nh1 / MKS-173) — a FLEX child of the root,
       * not a grid item. A grid item's containing block is its own grid area
       * (one row tall), which gives `position: sticky` zero travel; as a flex
       * child its containing block is the full-height flex container, so
       * `-top-4` can actually pin it while the body scrolls underneath.
       *
       * One continuous band (rather than pinning each header cell
       * individually) keeps the opaque background unbroken, so cards
       * scrolling underneath cannot show through the 12px column gutters.
       *
       * `-mt-4 pt-4 -top-4` compensates for the board scroll container's
       * `p-4` (KanbanBoardPage.tsx). Chrome rests a `top-0` sticky box at the
       * padding edge (16px down), leaving scrolled cards visible in the top
       * padding strip — verified empirically (MKS-173). `-top-4` pins the
       * band 16px higher so its opaque background covers that strip, `-mt-4`
       * keeps the unpinned layout unchanged, and `pt-4` re-adds the space so
       * header content never sits under the toolbar edge.
       * `pb-3` reproduces the 12px row gap the old single grid provided
       * between the header strip and the first body row.
       *
       * `z-20` clears unlayered board cards and stays under dnd-kit's
       * DragOverlay (inline `zIndex: 999`) and the task slide-over
       * (`fixed inset-0 z-50`). No overflow-clipping utility is added here —
       * StateColorPickerPopover renders `absolute top-full ... z-50` inside
       * ColumnHeaderCell and must be able to paint beyond the band's box.
       */}
      <div
        data-testid="kanban-column-headers"
        className={`sticky -top-4 z-20 -mt-4 pt-4 ${
          slimHeader ? 'pb-2' : 'pb-3'
        } bg-ms-canvas grid gap-3`}
        style={gridStyle}
      >
        {/* sticky-left corner cell (column 1); MKS-378 puts the slim/full header
            toggle here. MKS-500 moves the toggle to the board toolbar and drops
            the column-1 track altogether, so the cell is not rendered then. */}
        {!headerToggleInToolbar && (
          <div className="flex items-center">
            <button
              type="button"
              onClick={toggleSlimHeader}
              aria-pressed={slimHeader}
              aria-label={slimHeader ? 'Show the full column header' : 'Show a slim column header'}
              title={slimHeader ? 'Show the full column header' : 'Show a slim column header'}
              className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-ms-text-muted hover:text-ms-text hover:bg-ms-surface focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              {slimHeader ? (
                <ChevronDownIcon className="h-3.5 w-3.5" />
              ) : (
                <ChevronUpIcon className="h-3.5 w-3.5" />
              )}
              {slimHeader ? 'Full header' : 'Slim header'}
            </button>
          </div>
        )}
        <div
          className="contents"
          // The contents wrapper is transparent to the grid so SortableContext
          // children land in columns 2..N+1 next to the corner cell above.
        >
          <SortableContext
            items={visibleColumns.map((c) => `col-${c.taskStateId}`)}
            strategy={horizontalListSortingStrategy}
          >
            {visibleColumns.map((col, idx) => (
              <ColumnHeaderCell
                key={col.taskStateId}
                column={col}
                canReorderColumns={canReorderColumns}
                columnIndex={idx}
                isColumnCollapsed={columnCollapsed[idx] ?? false}
                onToggleColumnCollapse={() =>
                  onToggleCollapse(String(col.taskStateId), col.isTerminal ?? false)
                }
                slim={slimHeader}
                canManageStates={canManageStates}
                onRename={onRenameState}
                onRecolor={onRecolorState}
                onDelete={onDeleteState}
                onToggleTerminal={onToggleTerminalState}
                onToggleInProgress={onToggleInProgressState}
              />
            ))}
          </SortableContext>
          {/* Quick task 260610-kv6 — trailing AddStateColumn slot. Rendered
              OUTSIDE the SortableContext so it can't be dragged into the
              column order; it's a permanent trailing affordance. */}
          {/* MKS-378 — the slim header hides the "Add state" chip; the empty cell keeps its track. */}
          {slimHeader && trailingHeaderSlot ? <div /> : trailingHeaderSlot}
          {/* MKS-487 — last in the row: the columns the empty rule took out of
              the grid, one click from being shown again. Rendered only when
              there is at least one, and its track is added to the template on
              exactly the same condition. */}
          {hiddenColumns.length > 0 && (
            <HiddenColumnsStrip
              columns={hiddenColumns}
              onReveal={(columnKey) =>
                setRevealed({
                  projectId,
                  keys: revealedColumnKeys.includes(columnKey)
                    ? revealedColumnKeys
                    : [...revealedColumnKeys, columnKey],
                })
              }
              onRevealAll={() =>
                setRevealed({
                  projectId,
                  keys: boardData.columns.map((c) => String(c.taskStateId)),
                })
              }
            />
          )}
        </div>
      </div>

      {/* Body grid — shares the same COLUMN tracks as the header band above so
       * they stay aligned (both templates are built from the same
       * fixed-minimum 240px/44px/minmax(320px,1fr) tracks). MKS-184 adds row
       * tracks in fill mode only, plus `flex-1 min-h-0` so the grid gets the
       * board height left over under the header band — that definite height is
       * what the `minmax(0, 1fr)` cells row divides up. */}
      <div
        className={fillHeight ? 'grid gap-3 flex-1 min-h-0' : 'grid gap-3'}
        style={bodyGridStyle}
      >
        {/* Quick 260914-huu — vertical SortableContext for row-reorder drag.
         * SortableContext emits no DOM node, so wrapping it around the body
         * grid's children does not affect the grid layout. The items array
         * order must match render order; the synthetic "No milestone" row is
         * included for context-membership consistency even though it is
         * disabled (see KanbanSwimlaneRow's useSortable call). */}
        <SortableContext
          items={[
            ...(hideParentRows
              ? []
              : [...orderedMilestones.map((m) => `row-${m.id}`), 'row-no-milestone']),
            ...lanes.map((l) => `row-${subprojectLaneKey(l.id)}`),
          ]}
          strategy={verticalListSortingStrategy}
        >
        {/* Loading state for milestone list (above the always-present "No
         * milestone" row, per UI-SPEC § Loading and Error States).
         * Phase 41 Plan 41-17 — bg-gray-100 → bg-ms-canvas (v7 token). */}
        {milestonesQuery.isLoading && (
          <div
            aria-busy="true"
            aria-label="Loading milestones"
            className="col-span-full px-1 py-3 space-y-3"
            style={{ gridColumn: '1 / -1' }}
          >
            <div className="h-10 rounded bg-ms-canvas animate-pulse" />
            <div className="h-10 rounded bg-ms-canvas animate-pulse" />
          </div>
        )}

        {/* Error state — inline red banner with Retry button.
         * Phase 41 Plan 41-17 retain reason: red-600/red-50/red-200 are part of
         * the v7 semantic palette (Breach rose / error tone) per 41-01 D-Tokens
         * decision: "Tailwind defaults (gray-*, blue-*, red-*, emerald-*, sky-*,
         * etc.) are preserved untouched." */}
        {milestonesQuery.isError && (
          <div
            role="alert"
            className="mx-1 my-3 px-3 py-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg"
            style={{ gridColumn: '1 / -1' }}
          >
            Couldn’t load milestones.{' '}
            <button
              type="button"
              onClick={() => milestonesQuery.refetch()}
              className="text-primary-600 hover:underline font-medium"
            >
              Retry
            </button>
          </div>
        )}

        {/* Empty-state hint (PM only) when no milestones yet.
         * Phase 41 Plan 41-17 — text-gray-500 → text-ms-text-secondary. */}
        {!milestonesQuery.isLoading &&
          !milestonesQuery.isError &&
          milestones.length === 0 &&
          canManageMilestones &&
          !hideParentRows &&
          !hideEmptyMilestoneChrome && (
            <p
              className="px-1 py-3 text-sm text-ms-text-secondary italic"
              style={{ gridColumn: '1 / -1' }}
            >
              No milestones yet. Click &quot;+ New milestone&quot; in the toolbar
              to create one.
            </p>
          )}

        {/* Real milestone rows. Server default order is DESC start_date, then
         * createdAt DESC. Quick 260914-huu — `orderedMilestones` applies each
         * user's private stored overlay (`useMilestoneOrder`) on top of that
         * default: milestones with a stored position come first in stored
         * order, the rest follow in server-default relative order. Absent /
         * stale stored order → server default unchanged (milestoneOrder.ts). */}
        {!hideParentRows && orderedMilestones.map((m) => (
          <KanbanSwimlaneRow
            noLabelTrack={headerToggleInToolbar}
            key={`m-${m.id}`}
            rowKey={String(m.id)}
            milestone={m}
            columns={visibleParentColumns}
            collapsedFlags={columnCollapsed}
            byMilestoneId={counts.byMilestoneId}
            unassignedCount={counts.unassigned}
            projectId={projectId}
            audience={audience}
            canManageMilestones={canManageMilestones}
            isFolded={isFolded(String(m.id))}
            onToggleFold={() => onToggleFold(String(m.id))}
            canReorderRows={rowsReorderable}
            onEditMilestone={() => onEditMilestone?.(m)}
            onDeleteMilestone={() => onDeleteMilestone?.(m)}
            onCardClick={onCardClick}
            onFlip={onFlip}
            projectTaskTypeId={projectTaskTypeId}
            stalenessConfig={stalenessConfig}
            fillHeight={fillHeight}
            subprojectsById={subprojectsById}
          />
        ))}

        {/* Synthetic "No milestone" row — pinned at the bottom (CONTEXT D-13).
         * Always rendered, regardless of milestone list length, so unassigned
         * tasks have a home. Folds like any other row (CONTEXT D-17). */}
        {!hideParentRows && (
        <KanbanSwimlaneRow
          noLabelTrack={headerToggleInToolbar}
          key="m-none"
          rowKey="no-milestone"
          milestone={null}
          columns={visibleParentColumns}
          collapsedFlags={columnCollapsed}
          hideRowHeader={hideParentRowBanner}
          byMilestoneId={counts.byMilestoneId}
          unassignedCount={counts.unassigned}
          projectId={projectId}
          audience={audience}
          canManageMilestones={canManageMilestones}
          isFolded={isFolded('no-milestone')}
          onToggleFold={() => onToggleFold('no-milestone')}
          canReorderRows={rowsReorderable}
          onEditMilestone={() => undefined}
          onDeleteMilestone={() => undefined}
          onCardClick={onCardClick}
          onFlip={onFlip}
          projectTaskTypeId={projectTaskTypeId}
          stalenessConfig={stalenessConfig}
          fillHeight={fillHeight}
          subprojectsById={subprojectsById}
        />
        )}

        {/* MKS-419 — one row per subproject with cards, after the parent's rows. */}
        {lanes.map((lane) => (
          <KanbanSwimlaneRow
            noLabelTrack={headerToggleInToolbar}
            key={`sp-${lane.id}`}
            rowKey={subprojectLaneKey(lane.id)}
            milestone={null}
            lane={lane}
            columns={visibleColumns}
            collapsedFlags={columnCollapsed}
            byMilestoneId={counts.byMilestoneId}
            unassignedCount={counts.unassigned}
            projectId={projectId}
            audience={audience}
            canManageMilestones={false}
            isFolded={isFolded(subprojectLaneKey(lane.id))}
            onToggleFold={() => onToggleFold(subprojectLaneKey(lane.id))}
            canReorderRows={false}
            onEditMilestone={() => undefined}
            onDeleteMilestone={() => undefined}
            onCardClick={onCardClick}
            onFlip={onFlip}
            projectTaskTypeId={null}
            fillHeight={false}
            subprojectsById={subprojectsById}
          />
        ))}
        </SortableContext>
      </div>
    </div>
  );
}
